/**
 * FAQ data voor het contact pagina.
 * Elk item bevat een vraag, zoek-keywords en een HTML-antwoord.
 */

export interface FaqItem {
  vraag: string;
  antwoord: string;
  keywords: string[];
}

export const faqItems: FaqItem[] = [
  {
    vraag: 'Hoe meld ik een schade?',
    keywords: ['schade', 'meld', 'melden', 'melding', 'claim', 'claimen'],
    antwoord: `<ol>
      <li>Om een schade te melden ga je op de dashboardpagina naar "Schades claimen/inzien".</li>
      <li>Je zoekt de relatie op waarvoor de schade gemeld dient te worden.</li>
      <li>Je komt dan in een overzicht van de polissen van deze relatie. Je kiest de polis(sen) aan waarop de schade gemeld dient te worden en selecteer een schadedatum. Tevens is er de mogelijkheid om documenten te uploaden die betrekking hebben op de schade, bijvoorbeeld foto's van de schade of een offerte voor herstel.</li>
      <li>Daarna kun je kiezen of je de relatie digitaal de schadegegevens wilt laten invullen of dat je dit zelf doet.</li>
      <li>Wanneer je kiest om zelf de schadegegevens in te vullen, beantwoord je de vragen op het schadeformulier. Klik daarna op de button "Opslaan".</li>
      <li>Maak de schademelding definitief door te klikken op de button "Schade nu indienen". Ook is er de mogelijkheid om de ingevulde schadegegevens te laten inzien door de verzekerde relatie of de schadegegevens aan te passen.</li>
      <li>Vul het mailadres in waarnaar de correspondentie over het schadedossier dient te worden gemaild. Hierna is de schademelding definitief ingediend.</li>
    </ol>
    <p>Vanuit het schadedossier kun je extra informatie toevoegen en de status en afwikkeling van het dossier inzien.</p>`,
  },
  {
    vraag: 'Hoe kan verzekerde een schade aan ons terugbetalen?',
    keywords: ['schade', 'betalen', 'terugbetalen'],
    antwoord: `<ol>
      <li>Kijk in Surenet via het schadedossier na welk schadebedrag er uitgekeerd is. Je kan dat terug vinden in het schadedossier onder het kopje: Totaal uitgekeerd.</li>
      <li>Klik op het kopje Totaal uitgekeerd en vervolgens krijg je een overzicht van welke bedragen er zijn uitgekeerd. Als we expertisekosten hebben betaald dan hoeft verzekerde deze niet terug te betalen.</li>
      <li>Verzekerde kan het uitgekeerde schadebedrag onder vermelding van het schadenummer over maken op onze IBAN: NL81ABNA0497493187.</li>
      <li>Na ontvangst van het bedrag zullen we dit verwerken en de B/M weer in kracht herstellen.</li>
    </ol>`,
  },
  {
    vraag: 'Hoe kan ik het incassomoment aangeven / vastleggen?',
    keywords: ['incasso'],
    antwoord: `<p>SUREbusiness kent maandelijks 3 momenten waarop de premie automatisch geïncasseerd wordt: De <strong>5e, 15e en 28e</strong> van elke maand.</p>
    <p>Waarbij de vervaldagen 29, 30 en 31 worden geïncasseerd in de daaropvolgende maand.</p>
    <p>Dus:</p>
    <ul>
      <li>Vervaldagen 1 t/m 5 worden geïncasseerd op de 5e van dezelfde maand.</li>
      <li>Vervaldagen 6 t/m 15 worden geïncasseerd op de 15e van dezelfde maand.</li>
      <li>Vervaldagen 16 t/m 28 worden geïncasseerd op de 28e van dezelfde maand.</li>
      <li>Vervaldagen 29 t/m 31 worden geïncasseerd op de 5e van de volgende maand.</li>
    </ul>`,
  },
  {
    vraag: 'Hoe kan ik een subpolis of de gehele pakketpolis royeren?',
    keywords: ['royeren', 'beeindigen'],
    antwoord: `<ol>
      <li>Ga naar 'Muteren / Royeren' in het linker verticale menu.</li>
      <li>Kies of de bewuste subpolis en klik op optie royeren, of bij het hoofdpakket kies voor de optie "Royeer pakket".</li>
      <li>Vervolgens kom je in een keuze menu en kan je de reden voor mutatie doorgeven en de datum per wanneer deze moet royeren.</li>
      <li>Kies of vul zelf de royementsreden en –datum.</li>
      <li>Klik op verder en bevestig nogmaals het royement.</li>
    </ol>`,
  },
  {
    vraag: 'Hoe kan ik twee pakketten van dezelfde relatie samenvoegen?',
    keywords: ['samenvoegen', 'twee pakketten', 'product toevoegen', 'mutaties'],
    antwoord: `<p>Het is niet mogelijk om een subpolis in een pakket over te hangen naar een ander pakket. Wat wel kan is de subpolis in het ene pakket beëindigen en het betreffende product opnieuw in het andere pakket toevoegen.</p>
    <p>Neem de volgende 3 stappen:</p>
    <p><strong>Stap 1: Polis royeren</strong></p>
    <ol>
      <li>Ga naar 'Muteren / Royeren' in het linker verticale menu.</li>
      <li>Kies een zoekveld waarop je de te royeren polis kunt vinden.</li>
      <li>Klik op de regel waar de betreffende relatie staat.</li>
      <li>Selecteer nu het pakket waaruit de subpolis geroyeerd moet worden, of kies bij de opties voor het hele pakket royeren.</li>
      <li>Kies bij wijzigingsreden in dit geval 'Andere royementreden..' en vul in 'Royement ivm samenvoegen pakketten'.</li>
      <li>Klik onderaan op 'Mutatie starten'.</li>
    </ol>
    <p class="text-primary italic">Let op: Je moet eerst stap 1 volledig afronden, anders kiest het systeem bij het toevoegen van het product alsnog de verkeerde polis!</p>
    <p><strong>Stap 2: Product toevoegen</strong></p>
    <ol>
      <li>Ga naar 'Muteren/Royeren' het linker verticale menu. Zoek nu naar de relatie.</li>
      <li>Klik op de relatie.</li>
      <li>Klik bij het juiste pakket op 'Product toevoegen'. Je kiest in de bovenste groene balk MKB, ZZP, VVE of OG Beheer. Je komt dan in het keuzeprogramma van de diverse producten. Je klikt hier op alle producten die je wilt toevoegen en klikt op 'Product toevoegen'. Klik nu op 'Mutatie starten'. De verdere procedure is gelijk aan die van een nieuwe aanvraag. Na het invullen van de premievragen volgt een overzicht van alle maatschappijen waaruit je kunt kiezen. Mocht blijken dat het risico maatwerk vraagt, dan kun je dat daar aanvragen met de rode knop aan de rechterkant. Vermeld daarbij dat het om een pakketsamenvoeging gaat en dat je de geroyeerde subpolis wilt bijvoegen in dit pakket.</li>
    </ol>
    <p><strong>Stap 3: Uitval creëren (ter voorkoming van dubbele mutatiekosten)</strong></p>
    <p>Omdat je twee mutaties doet krijgt relatie ook twee keer kosten. Dat moet in dit geval niet. Daarom moet je de nieuwe mutatie 'Product toevoegen' laten uitvallen. Dat doe je met het invullen van de laatste acceptatievraag. Zet daarin de volgende tekst; 'Kosten tegenboeken i.v.m. samenvoegen pakketten.'</p>`,
  },
  {
    vraag: 'Hoe is de pakketkorting opgebouwd?',
    keywords: ['paketten', 'korting', 'pakketkorting'],
    antwoord: `<p>De pakketkorting is als volgt opgebouwd:</p>
    <ul>
      <li>Bij 3 subpolissen: 5,0%</li>
      <li>Bij 4 subpolissen: 7,5%</li>
      <li>Bij 5 subpolissen of meer: 10%</li>
    </ul>`,
  },
  {
    vraag: 'Hoe kan ik verzekerde waarde wijzigen?',
    keywords: ['verzekerde waarde'],
    antwoord: `<ol>
      <li>Ga naar 'Muteren / Royeren' in het linker verticale menu.</li>
      <li>Kies relatie en bewuste subpolis.</li>
      <li>Klik op opties kies voor muteren.</li>
      <li>Reden mutatie opgeven en datum (per wanneer moet deze wijzigen).</li>
      <li>Klik op verder.</li>
      <li>Vervolgens kom je in de offerte module en pas hier de "te verzekerde waarde" aan.</li>
      <li>Vervolg het traject alsof het gaat om een nieuwe aanvraag.</li>
    </ol>
    <p class="text-primary italic">Let op: Switchen naar een andere verzekeraar is mogelijk maar deze wordt hetzelfde behandeld als een nieuwe aanvraag.</p>`,
  },
  {
    vraag: 'Hoe kan ik polis laten aanpassen naar aanleiding van een taxatierapport?',
    keywords: ['taxatierapport', 'waarde rapport'],
    antwoord: `<p>Volg in beginsel dezelfde stappen als voor een wijziging van de verzekerde waarde, maar voer de volgende extra handeling uit:</p>
    <p><strong>Voordat je de mutatie definitief doorvoert</strong> moet je <u>het taxatierapport of het waarde rapport uploaden</u>.</p>
    <p>Je laat daarna de <u>aanvraag uitvallen door een slotvraag in te vullen</u> met b.v. de mededeling "Taxatie of waarde rapport van toepassing, clausule toevoegen".</p>
    <p>Wij pakken de wijziging dan verder op, voegen de clausule toe en geven de polis af.</p>`,
  },
  {
    vraag: 'Hoe kan ik een provisie percentage wijzigen?',
    keywords: ['provisie'],
    antwoord: `<ol>
      <li>Ga naar 'Muteren / Royeren' in het linker verticale menu.</li>
      <li>Kies relatie en bewuste subpolis.</li>
      <li>Klik op opties kies voor muteren.</li>
      <li>Vul in bij de reden mutatie "wijziging provisie en de datum van de wijziging.</li>
      <li>Klik op verder.</li>
      <li>Vervolg het traject alsof het gaat om een nieuwe aanvraag.</li>
      <li>In het pakket overzicht kan je de provisie aanpassen.</li>
      <li>Loop de verdere stappen van deze aanvraag door.</li>
    </ol>
    <p class="text-primary italic">Let op: Het switchen naar een andere verzekeraar is mogelijk maar deze wordt hetzelfde behandeld als een nieuwe aanvraag.</p>`,
  },
  {
    vraag: 'Hoe kan ik de tenaamstelling wijzigen?',
    keywords: ['naam', 'tenaamstelling'],
    antwoord: `<ol>
      <li>Ga naar 'Relatiegegevens' in het linker verticale menu.</li>
      <li>Zoek de betreffende relatie.</li>
      <li>Klik onder het tabje 'Algemeen', onderaan de pagina op de groene button 'Wijzigen'.</li>
      <li>Wijzig de gegevens zoals gewenst en sla dit weer op via de groene button 'Opslaan' onderaan de pagina.</li>
    </ol>
    <p><strong>LET OP!</strong> Bij naamswijziging mag het niet gaan om een wijziging van eigenaar of rechtspersoon. Zodra er sprake is van een andere eigenaar/rechtspersoon dient de bestaande verzekering beëindigd te worden en op naam van de nieuwe eigenaar/rechtspersoon een nieuwe verzekering te worden aangevraagd. Dit moet zodat de nieuwe eigenaar/rechtspersoon de slotvragen zelf invult en wij de verplichte screening juist kunnen uitvoeren.</p>`,
  },
  {
    vraag: 'Hoe kan ik een adres wijzigen en daarna de polis afgeven?',
    keywords: ['adres'],
    antwoord: `<ol>
      <li>Ga naar 'Muteren / Royeren' in het linker verticale menu.</li>
      <li>Kies relatie en bewuste subpolis.</li>
      <li>Klik op opties kies voor muteren.</li>
      <li>Vul in bij reden mutatie "wijzig risicoadres" en de datum van de wijziging.</li>
      <li>Klik op verder.</li>
      <li>Vervolgens kom je in de offerte module en pas hier de "postcode/huisnr/toevoeging" aan.</li>
      <li>Vervolg het traject alsof het gaat om een nieuwe aanvraag.</li>
    </ol>
    <p class="text-primary italic">Let op: Het switchen naar een andere verzekeraar is mogelijk maar deze wordt hetzelfde behandeld als een nieuwe aanvraag.</p>`,
  },
  {
    vraag: 'Hoe maak ik zelf een offerte?',
    keywords: ['offerte', 'offertes'],
    antwoord: `<ol>
      <li>Om een offerte aan te vragen ga je naar Offreren/aanvragen.</li>
      <li>Klik op 'Nieuwe offerte'.</li>
      <li>Je kiest MKB, ZZP, VVE of OG Beheer.</li>
      <li>Je klikt op alle producten die je wilt offreren en klikt op 'Start'.</li>
      <li>Daarna wijst het programma zich vanzelf alsof je een nieuwe aanvraag doet.</li>
      <li>Je kunt direct een bestaande relatie koppelen aan de offerte of een nieuwe relatie toevoegen.</li>
      <li>Na het kiezen van de producten en de relatie volgt een overzicht van alle maatschappijen waaruit je kunt kiezen.</li>
    </ol>
    <p>Mocht blijken dat het risico maatwerk vereist, dan kun je dat aanvragen met de rode knop aan de rechter kant.</p>`,
  },
  {
    vraag: 'Hoe kan ik informatie over een relatie vinden?',
    keywords: ['relatie'],
    antwoord: `<p>Je kunt deze informatie heel makkelijk opvragen:</p>
    <ol>
      <li>Ga naar 'Relatiegegevens' in het linker verticale menu.</li>
      <li>Zoek de betreffende relatie.</li>
      <li>Kijk bij het tabblad 'Algemeen'. Hier zie je alle NAW gegevens die bekend zijn. Je kunt ze hier ook zelf heel makkelijk wijzigen via de button wijzigen onderaan het scherm.</li>
      <li>Je ziet in het verticale menu de volgende tabbladen: Algemeen, Offertes, Aanvragen, Polissen, Schades, Incasso, Alerts, Historie, Documenten, Prolongatiewijzigingen en Memo's.</li>
      <li>Klik op 'Documenten'.</li>
      <li>Verticaal zie je nu:
        <ul>
          <li><strong>Polissen</strong> - Hier vind je alle polis aanhangsels, groene kaarten en correspondentie.</li>
          <li><strong>Schades</strong> - Hier vind je alles met betrekking tot schades van deze relatie.</li>
          <li><strong>Relatie</strong> - Hier vind je alle aanvragen en offertes.</li>
          <li><strong>Incasso</strong> - Hier vind je alles met betrekking tot incasso's.</li>
          <li><strong>Facturen</strong> - Hier vind je alle incassofacturen.</li>
        </ul>
      </li>
    </ol>
    <p>Je kunt hier ook zelf makkelijk documenten toevoegen middels 'Documenten uploaden' als je dat nodig of handig vindt.</p>`,
  },
  {
    vraag: 'Hoe kan ik ons eigen email adres wijzigen in de bevestigingen?',
    keywords: ['email'],
    antwoord: `<ol>
      <li>Ga in de linker balk naar 'Instellingen'.</li>
      <li>Ga naar het tabje Offertes/Aanvragen/Schades.</li>
      <li>Ga links naar e-mail adressen.</li>
      <li>Je ziet dan drie opties: email bevestiging aanvraag, email bevestiging polisstukken en email bevestiging maatwerk.</li>
      <li>Wijzig hier het gewenste email adres en sla op met de groene button 'Opslaan'.</li>
    </ol>`,
  },
  {
    vraag: 'Hoe kan ik de openstaande premies of premie-achterstand bekijken?',
    keywords: ['openstaande premie', 'achterstand', 'achterstallig'],
    antwoord: `<ul>
      <li>Onder 'Overzichten' in de linker verticale menubalk kunt u een overzicht downloaden met alle openstaande premies van uw relaties.</li>
      <li>Voor een individuele relatie:
        <ol>
          <li>Kies in de linker verticale menubalk 'Relatiegegevens'.</li>
          <li>Zoek en kies de betreffende relatie.</li>
          <li>Open de relatie door er op te klikken.</li>
          <li>Kies het tabblad 'Incasso'. Als er achterstanden zijn dan is het tabje oranje (dan is er een 1e betalingsherinnering verzonden) of rood (2e betalingsherinnering verzonden en polis opgeschort). Na klikken op het tabje zie je de incasso bedragen en de boekingen.</li>
        </ol>
      </li>
    </ul>`,
  },
  {
    vraag: 'Hoe kan ik de polisvoorwaarden en clausules van een polis bekijken?',
    keywords: ['polisvoorwaarden', 'clausules'],
    antwoord: `<ol>
      <li>Kies in de linker verticale menubalk 'Relatiegegevens'.</li>
      <li>Zoek en kies de betreffende relatie.</li>
      <li>Open de relatie door er op te klikken.</li>
      <li>Kies het tabblad 'Polissen'.</li>
      <li>Klik op de betreffende pakketpolis om deze te openen.</li>
      <li>Klik op de gewenste subpolis en navigeer naar het relevante tabblad om de gewenste voorwaarden en/of clausules in te zien.</li>
    </ol>`,
  },
  {
    vraag: 'Hoe kan ik de (afgegeven) groene kaarten vinden?',
    keywords: ['groene kaart'],
    antwoord: `<ol>
      <li>Kies in de linker verticale menubalk 'Relatiegegevens'.</li>
      <li>Zoek en kies de betreffende relatie.</li>
      <li>Open de relatie door er op te klikken.</li>
      <li>Kies het tabblad 'Documenten'. Kijk vervolgens onder het tabje 'Polissen' en zoek het betreffende voertuig en klik er op.</li>
      <li>Vervolgens zijn alle documenten zichtbaar. De groene kaart staat onder de kop 'IVB/VHD-kaart' en kun je downloaden met de groene knop.</li>
    </ol>`,
  },
  {
    vraag: 'Hoe kan ik een rekening courant vinden en downloaden?',
    keywords: ['rekening courant'],
    antwoord: `<ol>
      <li>Kies in de linker verticale menubalk 'Overzichten'.</li>
      <li>Zoek en kies 'Rekening Courant'.</li>
      <li>Dit scherm vertoont alle rekening courant overzichten. Met de 'Download' knop kun je de PDF ophalen van de betreffende maand.</li>
    </ol>`,
  },
  {
    vraag: 'Hoe kan ik een prolongatieborderel vinden en downloaden?',
    keywords: ['prolongatieborderel'],
    antwoord: `<ol>
      <li>Kies in de linker verticale menubalk 'Overzichten'.</li>
      <li>Zoek en kies 'Prolongatie borderellen'.</li>
      <li>Dit scherm vertoont per maand de prolongatie borderellen. Met de 'Download' knop kun je de PDF ophalen van de betreffende maand.</li>
    </ol>`,
  },
  {
    vraag: 'Hoe kan ik een factuur van een relatie vinden en downloaden?',
    keywords: ['factuur', 'betaalgegevens', 'facturen'],
    antwoord: `<ol>
      <li>Kies in de linker verticale menubalk 'Relatiegegevens'.</li>
      <li>Zoek en kies de betreffende relatie.</li>
      <li>Open de relatie door er op te klikken.</li>
      <li>Kies in het verticale menu 'Documenten'.</li>
      <li>Selecteer het tabblad 'Facturen'.</li>
    </ol>`,
  },
  {
    vraag: 'Hoe wijzig ik een rekeningnummer van de klant?',
    keywords: ['rekeningnummer wijzigen', 'IBAN', 'bank'],
    antwoord: `<ol>
      <li>Kies in de linker verticale menubalk 'Relatiegegevens'.</li>
      <li>Zoek en kies de betreffende relatie.</li>
      <li>Open de relatie door er op te klikken.</li>
      <li>Kies het tabblad horizontaal 'Algemeen'.</li>
      <li>Kies het tabblad verticaal 'Relatiegegevens'.</li>
      <li>Onderaan het scherm kies je de knop 'Wijzigen'.</li>
      <li>Wijzig het nummer bij 'IBAN'.</li>
    </ol>
    <p><strong>Let op:</strong> Indien het gaat om automatische incasso dan moet de rekeninghouder expliciet akkoord (hebben ge-)geven.</p>`,
  },
  {
    vraag: 'Hoe kies ik de juiste hoedanigheid / SBI code?',
    keywords: ['hoedanigheid', 'SBI'],
    antwoord: `<ol>
      <li>Check op basis van vrije tekst de werkzaamheden bij het SBI website: <a href="http://sbi.cbs.nl" target="_blank" class="text-primary underline">www.sbi.cbs.nl</a></li>
      <li>Voer bij voorkeur geen overige in dit kan ervoor zorgen dat er geen premie tot stand komt. Overleg met acceptatie of stuur deze in als maatwerk.</li>
    </ol>`,
  },
  {
    vraag: 'Hoe kan ik een hoedanigheid wijzigen op een bestaande polis?',
    keywords: ['hoedanigheid'],
    antwoord: `<ol>
      <li>Ga naar 'Muteren / Royeren' in het linker verticale menu.</li>
      <li>Kies de bewuste subpolis en klik op opties.</li>
      <li>Kies voor muteren.</li>
      <li>Optie "andere reden".</li>
      <li>Vul de tekst "aanpassing hoedanigheid" in.</li>
      <li>Klik onderaan op verder.</li>
      <li>Vervolgens kom je in de offerte module en pas hier de hoedanigheid per maatschappij aan.</li>
      <li>Vervolg het traject alsof het gaat om een nieuwe aanvraag.</li>
    </ol>
    <p class="text-primary italic">Let op: Het switchen naar een andere verzekeraar is mogelijk maar deze wordt hetzelfde behandeld als een nieuwe aanvraag.</p>`,
  },
  {
    vraag: 'Hoe kan ik een polis van intermediair wijzigen?',
    keywords: ['intermediair wijzigen'],
    antwoord: `<p>Mail het verzoek naar <a href="mailto:backoffice@surebusiness.nl">backoffice@surebusiness.nl</a></p>`,
  },
];
